import React, { useEffect, useMemo, useState } from "react";
import _ from "lodash";
import {
    Button,
    Card,
    CardContent,
    Typography,
    Checkbox,
    FormControlLabel,
} from "@mui/material";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";

type Role = "root" | "client" | "supervisor" | "qaworker";

type SummaryItem = {
    title: string;
    key: string;
    isarray: boolean;
    roles: Role[];
};

type SummaryMap = {
    [surveyId: number]: SummaryItem[];
};

type Props = {
    project: any;
    surveys: any[];
    onSave: (summary: SummaryMap) => void;
};

const DEFAULT_ROLES: Role[] = ["root", "client", "supervisor", "qaworker"];

const isArrayByType = (type: string) =>
    ["multi-select", "multi-select-other"].includes(type);

const beautify = (str: string) =>
    str.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());

/* ================= COMPONENT ================= */
export const AnalyticalSummary: React.FC<Props> = ({
    project,
    surveys,
    onSave,
}) => {
    const [selectedSurveyId, setSelectedSurveyId] = useState<number | "">("");
    const [summaryMap, setSummaryMap] = useState<SummaryMap>({});

    /* ================= PREFILL ================= */
    const existingSummaryMap: SummaryMap = useMemo(
        () =>
            _.get(project, "properties.analytical_report_fields_v2.value", {}),
        [project]
    );

    useEffect(() => {
        if (!_.isEmpty(existingSummaryMap)) {
            setSummaryMap(existingSummaryMap);
        }
    }, [existingSummaryMap]);

    /* ================= SELECTED SURVEY ================= */
    const selectedSurvey = useMemo(
        () => surveys.find((s) => s.id === selectedSurveyId),
        [selectedSurveyId, surveys]
    );

    /* ================= DISPLAY CONTROL (🔥 FIXED) ================= */
    const summariesToRender = useMemo(() => {
        if (selectedSurveyId) {
            return {
                [selectedSurveyId]: summaryMap[selectedSurveyId] || [],
            };
        }
        return summaryMap;
    }, [selectedSurveyId, summaryMap]);

    /* ================= GENERATE FROM SURVEY ================= */
    const generateFromSurvey = () => {
        if (!selectedSurvey?.questions || !selectedSurveyId) return;

        setSummaryMap((prev) => {
            const existing = prev[selectedSurveyId] || [];
            const existingKeys = new Set(existing.map((i) => i.key));

            const generated = selectedSurvey.questions
                .flatMap((q: any) => {
                    if (q.type === "profile") return [];

                    if (q.type === "section-wise") {
                        const fields = q.payload?.sections?.[0]?.fields || [];

                        return fields.map((field: any) => ({
                            title: `${beautify(q.name)} (${beautify(
                                field.key
                            )})`,
                            key: `qr_${q.name}_${field.key}`,
                            isarray: false,
                            roles: DEFAULT_ROLES,
                        }));
                    }

                    const isArray = isArrayByType(q.type);

                    return [
                        {
                            title: beautify(q.name),
                            key: `qr_${q.name}${isArray ? "_arr_" : ""}`,
                            isarray: isArray,
                            roles: DEFAULT_ROLES,
                        },
                    ];
                })
                .filter((i) => !existingKeys.has(i.key));

            return {
                ...prev,
                [selectedSurveyId]: [...existing, ...generated],
            };
        });
    };

    /* ================= PROFILE FIELDS ================= */
    const generateProfileFields = () => {
        if (!selectedSurveyId) return;

        const profileFields: SummaryItem[] = [
            {
                title: "Gender",
                key: "profile_gender",
                isarray: false,
                roles: DEFAULT_ROLES,
            },
            {
                title: "Caste",
                key: "profile_caste_l0",
                isarray: false,
                roles: DEFAULT_ROLES,
            },
            {
                title: "Age",
                key: "profile_age",
                isarray: false,
                roles: DEFAULT_ROLES,
            },
            {
                title: "Occupation",
                key: "profile_occupation",
                isarray: false,
                roles: DEFAULT_ROLES,
            },
            {
                title: "Economic Status",
                key: "profile_economic_status",
                isarray: false,
                roles: DEFAULT_ROLES,
            },
        ];

        setSummaryMap((prev) => {
            const existing = prev[selectedSurveyId] || [];
            const keys = new Set(existing.map((i) => i.key));

            return {
                ...prev,
                [selectedSurveyId]: [
                    ...existing,
                    ...profileFields.filter((p) => !keys.has(p.key)),
                ],
            };
        });
    };

    /* ================= ACTIONS ================= */
    const toggleRole = (surveyId: number, index: number, role: Role) => {
        setSummaryMap((prev) => {
            const clone = [...(prev[surveyId] || [])];
            clone[index] = {
                ...clone[index],
                roles: clone[index].roles.includes(role)
                    ? clone[index].roles.filter((r) => r !== role)
                    : [...clone[index].roles, role],
            };
            return { ...prev, [surveyId]: clone };
        });
    };

    const removeItem = (surveyId: number, index: number) => {
        setSummaryMap((prev) => ({
            ...prev,
            [surveyId]: prev[surveyId].filter((_, i) => i !== index),
        }));
    };

    const deleteSurveySummary = (surveyId: number) => {
        setSummaryMap((prev) => {
            const clone = { ...prev };
            delete clone[surveyId];
            return clone;
        });

        if (selectedSurveyId === surveyId) {
            setSelectedSurveyId("");
        }
    };

    /* ================= UI ================= */
    return (
        <div className="p-4 space-y-5 min-h-0">
            <div className="flex justify-between items-center">
                <Typography variant="h5" fontWeight="bold">
                    Analytical Summary
                </Typography>

                <Button variant="contained" onClick={() => onSave(summaryMap)}>
                    Save
                </Button>
            </div>

            <Card>
                <CardContent className="space-y-3">
                    <Typography fontWeight="bold">
                        Survey Based Analytics
                    </Typography>

                    <Select
                        fullWidth
                        value={selectedSurveyId}
                        displayEmpty
                        onChange={(e) =>
                            setSelectedSurveyId(e.target.value as number)
                        }
                    >
                        <MenuItem value="">
                            <em>All Surveys</em>
                        </MenuItem>
                        {surveys.map((s) => (
                            <MenuItem key={s.id} value={s.id}>
                                {s.name}
                            </MenuItem>
                        ))}
                    </Select>

                    <div className="flex gap-2">
                        <Button
                            variant="outlined"
                            disabled={!selectedSurvey}
                            onClick={generateFromSurvey}
                        >
                            Generate From Survey
                        </Button>

                        <Button
                            variant="outlined"
                            color="secondary"
                            disabled={!selectedSurveyId}
                            onClick={generateProfileFields}
                        >
                            Generate Profile Fields
                        </Button>
                    </div>
                </CardContent>
            </Card>

            {Object.entries(summariesToRender).map(([surveyId, items]) => {
                const survey = surveys.find((s) => s.id === Number(surveyId));
                if (!items.length) return null;

                return (
                    <Card key={surveyId}>
                        <CardContent>
                            <div className="flex justify-between items-center mb-3">
                                <Typography fontWeight="bold">
                                    {survey?.name || "Survey"} – Analytics
                                </Typography>

                                <Button
                                    size="small"
                                    color="error"
                                    onClick={() =>
                                        deleteSurveySummary(Number(surveyId))
                                    }
                                >
                                    Delete Summary
                                </Button>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 max-h-[420px] overflow-auto">
                                {items.map((item, index) => (
                                    <div
                                        key={index}
                                        className="border rounded p-3"
                                    >
                                        <div className="flex justify-between">
                                            <div>
                                                <div className="font-semibold">
                                                    {item.title}
                                                </div>
                                                <div className="text-xs text-gray-500">
                                                    {item.key} |{" "}
                                                    {item.isarray
                                                        ? "Multi"
                                                        : "Single"}
                                                </div>
                                            </div>

                                            <Button
                                                size="small"
                                                color="error"
                                                onClick={() =>
                                                    removeItem(
                                                        Number(surveyId),
                                                        index
                                                    )
                                                }
                                            >
                                                Remove
                                            </Button>
                                        </div>

                                        <div className="flex flex-wrap gap-2 mt-3">
                                            {DEFAULT_ROLES.map((r) => (
                                                <FormControlLabel
                                                    key={r}
                                                    control={
                                                        <Checkbox
                                                            size="small"
                                                            checked={item.roles.includes(
                                                                r
                                                            )}
                                                            onChange={() =>
                                                                toggleRole(
                                                                    Number(
                                                                        surveyId
                                                                    ),
                                                                    index,
                                                                    r
                                                                )
                                                            }
                                                        />
                                                    }
                                                    label={r}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </CardContent>
                    </Card>
                );
            })}
        </div>
    );
};
